/**
 * Partner listing vehicle identity form state and payload helpers (Milestone 5).
 */

export const IDENTITY_MODE = Object.freeze({
  SELECTOR: "selector",
  MANUAL: "manual",
});

export function emptyVehicleIdentityState() {
  return {
    mode: IDENTITY_MODE.SELECTOR,
    makeId: null,
    modelId: null,
    trimId: null,
    makeLabel: null,
    modelLabel: null,
    trimLabel: null,
    makeRaw: "",
    modelRaw: "",
    trimRaw: "",
    normalizationStatus: null,
    needsReview: false,
  };
}

export function vehicleIdentityFromListing(listing = {}) {
  const hasCanonical = Boolean(listing.makeId && listing.modelId);
  const hasRaw = Boolean(listing.makeRaw || listing.make || listing.modelRaw || listing.model);

  if (hasCanonical) {
    return {
      mode: IDENTITY_MODE.SELECTOR,
      makeId: listing.makeId,
      modelId: listing.modelId,
      trimId: listing.trimId || null,
      makeLabel: listing.makeLabel || listing.normalizedMake || listing.make || "",
      modelLabel: listing.modelLabel || listing.normalizedModel || listing.model || "",
      trimLabel: listing.trimLabel || listing.normalizedVersion || listing.version || "",
      makeRaw: listing.makeRaw || listing.make || listing.makeLabel || "",
      modelRaw: listing.modelRaw || listing.model || listing.modelLabel || "",
      trimRaw: listing.trimRaw || listing.version || listing.trimLabel || "",
      normalizationStatus: listing.normalizationStatus || null,
      needsReview: listing.normalizationStatus === "partial" || listing.normalizationStatus === "unmatched",
    };
  }

  if (hasRaw) {
    return {
      mode: IDENTITY_MODE.MANUAL,
      makeId: null,
      modelId: null,
      trimId: null,
      makeLabel: null,
      modelLabel: null,
      trimLabel: null,
      makeRaw: listing.makeRaw || listing.make || "",
      modelRaw: listing.modelRaw || listing.model || "",
      trimRaw: listing.trimRaw || listing.version || "",
      normalizationStatus: listing.normalizationStatus || "unmatched",
      needsReview: true,
    };
  }

  return emptyVehicleIdentityState();
}

export function partnerVehicleDisplayName(identity = {}, year = "") {
  const displayMake = identity.makeLabel || identity.makeRaw || "";
  const displayModel = identity.modelLabel || identity.modelRaw || "";
  const displayTrim = identity.trimLabel || identity.trimRaw || "";
  return [year, displayMake, displayModel, displayTrim].filter(Boolean).join(" ").trim();
}

export function validateVehicleIdentity(identity = {}) {
  const errors = [];
  const makeValue = identity.mode === IDENTITY_MODE.MANUAL ? identity.makeRaw : identity.makeLabel;
  const modelValue = identity.mode === IDENTITY_MODE.MANUAL ? identity.modelRaw : identity.modelLabel;

  if (!String(makeValue || "").trim()) errors.push("Make is required.");
  if (!String(modelValue || "").trim()) errors.push("Model is required.");

  if (identity.mode === IDENTITY_MODE.SELECTOR) {
    if (!identity.makeId) errors.push("Select a make from the list or use manual entry.");
    if (!identity.modelId) errors.push("Select a model from the list or use manual entry.");
  }

  return errors;
}

export function buildVehicleIdentitySubmitPayload(identity = {}) {
  const makeDisplay =
    identity.mode === IDENTITY_MODE.MANUAL
      ? String(identity.makeRaw || "").trim()
      : String(identity.makeLabel || identity.makeRaw || "").trim();
  const modelDisplay =
    identity.mode === IDENTITY_MODE.MANUAL
      ? String(identity.modelRaw || "").trim()
      : String(identity.modelLabel || identity.modelRaw || "").trim();
  const trimDisplay =
    identity.mode === IDENTITY_MODE.MANUAL
      ? String(identity.trimRaw || "").trim()
      : String(identity.trimLabel || identity.trimRaw || "").trim();

  const useSelector = identity.mode === IDENTITY_MODE.SELECTOR;

  return {
    make: makeDisplay,
    model: modelDisplay,
    version: trimDisplay || null,

    makeRaw: identity.makeRaw || makeDisplay,
    modelRaw: identity.modelRaw || modelDisplay,
    trimRaw: identity.trimRaw || trimDisplay || null,

    makeId: useSelector ? identity.makeId : null,
    modelId: useSelector ? identity.modelId : null,
    trimId: useSelector ? identity.trimId || null : null,

    makeLabel: useSelector ? identity.makeLabel || makeDisplay : null,
    modelLabel: useSelector ? identity.modelLabel || modelDisplay : null,
    trimLabel: useSelector ? identity.trimLabel || trimDisplay || null : null,

    normalizationSource: useSelector ? "selector" : "manual_fallback",
    manualIdentityFallback: !useSelector,
  };
}

export function clearModelAndTrim(identity) {
  return {
    ...identity,
    modelId: null,
    modelLabel: null,
    modelRaw: "",
    trimId: null,
    trimLabel: null,
    trimRaw: "",
  };
}

export function clearTrim(identity) {
  return {
    ...identity,
    trimId: null,
    trimLabel: null,
    trimRaw: "",
  };
}

export function applyMakeSelection(identity, make) {
  return clearModelAndTrim({
    ...identity,
    mode: IDENTITY_MODE.SELECTOR,
    makeId: make.id,
    makeLabel: make.label,
    makeRaw: make.label,
    needsReview: false,
  });
}

export function applyModelSelection(identity, model) {
  return clearTrim({
    ...identity,
    mode: IDENTITY_MODE.SELECTOR,
    modelId: model.id,
    modelLabel: model.label,
    modelRaw: model.label,
    needsReview: false,
  });
}

export function applyTrimSelection(identity, trim) {
  return {
    ...identity,
    mode: IDENTITY_MODE.SELECTOR,
    trimId: trim?.id || null,
    trimLabel: trim?.label || null,
    trimRaw: trim?.label || "",
    needsReview: false,
  };
}

export function switchToManualIdentity(identity) {
  return {
    ...identity,
    mode: IDENTITY_MODE.MANUAL,
    makeId: null,
    modelId: null,
    trimId: null,
    makeLabel: null,
    modelLabel: null,
    trimLabel: null,
    makeRaw: identity.makeRaw || identity.makeLabel || "",
    modelRaw: identity.modelRaw || identity.modelLabel || "",
    trimRaw: identity.trimRaw || identity.trimLabel || "",
    needsReview: true,
  };
}

export function switchToSelectorIdentity(identity) {
  return {
    ...identity,
    mode: IDENTITY_MODE.SELECTOR,
    needsReview: false,
  };
}
